import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Minus, Plus, ShoppingBag } from "lucide-react";
import { Helmet } from "react-helmet-async";
import useEmblaCarousel from "embla-carousel-react";
import { toast } from "sonner";
import ProductCard, { productBadges } from "@/components/ProductCard";
import Reveal from "@/components/Reveal";
import { useShop } from "@/context/ShopContext";
import { inr } from "@/lib/api";

const ProductDetail = () => {
  const { id } = useParams();
  const { products, loadingProducts, addToCart, setCartOpen } = useShop();
  const [qty, setQty] = useState(1);
  const [selected, setSelected] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  const product = products.find((p) => p.id === id);
  const images = useMemo(
    () => (product ? (product.images?.length ? product.images : [product.image]) : []),
    [product]
  );
  const related = useMemo(
    () => (product ? products.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4) : []),
    [products, product]
  );

  useEffect(() => {
    setSelected(0);
    emblaApi?.scrollTo(0, true);
  }, [id, emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  if (loadingProducts) {
    return <div className="mx-auto max-w-7xl px-5 pb-24 pt-36 sm:px-8"><div className="aspect-[4/5] max-w-xl animate-pulse rounded-2xl bg-sand" /></div>;
  }
  if (!product) {
    return (
      <div className="mx-auto max-w-7xl px-5 pb-24 pt-40 text-center sm:px-8">
        <p className="font-serif text-3xl text-espresso">This piece has found a home</p>
        <Link to="/shop" className="mt-6 inline-flex items-center gap-2 rounded-full bg-espresso px-6 py-3 text-sm font-semibold text-cream hover:bg-terra">
          <ArrowLeft className="h-4 w-4" /> Back to shop
        </Link>
      </div>
    );
  }

  const soldOut = product.stock === 0;
  const badges = productBadges(product);
  const specs = Object.entries(product.specs || {}).filter(([k]) => k !== "Firing");

  const handleAdd = () => {
    addToCart(product, qty);
    toast.success(`${product.name} × ${qty} added to cart`);
    setCartOpen(true);
  };

  return (
    <div className="mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8 lg:pt-36" data-testid="product-detail-page">
      <Helmet>
        <title>{`${product.name} | Riaas Collective`}</title>
        <meta name="description" content={product.description} />
        <meta property="og:title" content={`${product.name} | Riaas Collective`} />
        <meta property="og:description" content={product.description} />
      </Helmet>

      <Link to="/shop" className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] text-clay/60 transition-colors hover:text-terra">
        <ArrowLeft className="h-3.5 w-3.5" /> All ceramics
      </Link>

      <div className="mt-8 grid gap-12 lg:grid-cols-2">
        <Reveal>
          <div className="relative overflow-hidden rounded-2xl bg-sand" ref={emblaRef} data-testid="product-gallery">
            <div className="flex">
              {images.map((src, i) => (
                <div key={i} className="min-w-0 flex-[0_0_100%]">
                  <img src={src} alt={`${product.name} — photo ${i + 1} of ${images.length}`} className="aspect-[4/5] w-full object-cover" draggable={false} />
                </div>
              ))}
            </div>
            <div className="pointer-events-none absolute left-4 top-4 flex flex-col items-start gap-1.5">
              {badges.filter((b) => b.label === "Sale" || b.label === "Bestseller").map((b) => (
                <span key={b.label} className={`rounded-full px-3.5 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest ${b.cls}`}>
                  {b.label}
                </span>
              ))}
            </div>
            {images.length > 1 && (
              <>
                <button
                  onClick={() => emblaApi?.scrollPrev()}
                  aria-label="Previous photo"
                  data-testid="gallery-prev"
                  className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-espresso shadow-md transition-colors hover:bg-terra hover:text-white"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => emblaApi?.scrollNext()}
                  aria-label="Next photo"
                  data-testid="gallery-next"
                  className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-espresso shadow-md transition-colors hover:bg-terra hover:text-white"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </>
            )}
          </div>
          {images.length > 1 && (
            <div className="mt-3 flex gap-3" data-testid="gallery-thumbs">
              {images.map((src, i) => (
                <button
                  key={i}
                  onClick={() => emblaApi?.scrollTo(i)}
                  aria-label={`Show photo ${i + 1}`}
                  className={`overflow-hidden rounded-xl transition-all ${selected === i ? "ring-2 ring-terra ring-offset-2 ring-offset-cream" : "opacity-60 hover:opacity-100"}`}
                >
                  <img src={src} alt="" className="h-16 w-16 object-cover" />
                </button>
              ))}
            </div>
          )}
        </Reveal>

        <Reveal delay={0.1}>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.28em] text-terra">{product.category}</p>
          <h1 className="mt-3 font-serif text-3xl leading-tight tracking-tight text-espresso sm:text-4xl">{product.name}</h1>
          <div className="mt-4 flex items-baseline gap-3">
            <span className="font-mono text-2xl font-semibold text-espresso">{inr(product.price)}</span>
            {product.original_price > product.price && (
              <span className="font-mono text-base text-clay/45 line-through">{inr(product.original_price)}</span>
            )}
          </div>

          {badges.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="product-badges">
              {badges.map((b) => (
                <span key={b.label} className={`rounded-full px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest ${b.cls}`}>
                  {b.label}
                </span>
              ))}
            </div>
          )}

          <p className="mt-5 max-w-md text-base leading-relaxed text-clay/80">{product.description}</p>
          <p className="mt-3 font-mono text-xs uppercase tracking-[0.18em] text-clay/60">Finish · {product.color}</p>

          <div className="mt-5">
            {soldOut ? (
              <span className="inline-block rounded-full bg-clay/10 px-4 py-1.5 font-mono text-xs uppercase tracking-[0.18em] text-clay">Sold out — back soon</span>
            ) : product.stock < 8 ? (
              <span className="inline-block rounded-full bg-terra-light px-4 py-1.5 font-mono text-xs uppercase tracking-[0.18em] text-terra-dark">Only {product.stock} left</span>
            ) : (
              <span className="inline-block rounded-full bg-sage-light px-4 py-1.5 font-mono text-xs uppercase tracking-[0.18em] text-sage">In stock · ready to ship</span>
            )}
          </div>

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center rounded-full border border-linen bg-white">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3.5 text-clay transition-colors hover:text-terra" aria-label="Decrease quantity" data-testid="detail-qty-minus">
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-8 text-center font-mono text-base">{qty}</span>
              <button onClick={() => setQty(Math.min(product.stock, qty + 1))} className="p-3.5 text-clay transition-colors hover:text-terra" aria-label="Increase quantity" data-testid="detail-qty-plus">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <button
              data-testid="detail-add-to-cart-button"
              onClick={handleAdd}
              disabled={soldOut}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-espresso py-4 text-sm font-semibold text-cream transition-colors duration-200 hover:bg-terra active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ShoppingBag className="h-4 w-4" /> Add to basket — {inr(product.price * qty)}
            </button>
          </div>

          {specs.length > 0 && (
            <div className="mt-10 overflow-hidden rounded-2xl border border-linen bg-white">
              <p className="border-b border-linen bg-sand/60 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.25em] text-clay/70">Details &amp; care</p>
              <dl>
                {specs.map(([k, v], i) => (
                  <div key={k} className={`flex justify-between gap-6 px-5 py-3 text-sm ${i !== specs.length - 1 ? "border-b border-linen/70" : ""}`}>
                    <dt className="font-mono text-[11px] uppercase tracking-[0.16em] text-clay/60">{k}</dt>
                    <dd className="text-right font-medium text-espresso">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          <p className="mt-6 rounded-2xl border border-dashed border-terra/40 bg-terra-light/50 px-5 py-4 text-xs leading-relaxed text-clay/80">
            <span className="font-semibold text-terra-dark">Good to know:</span> each piece is individually handmade —
            expect gentle variations in glaze, tone and form. That's what makes yours one of a kind.
          </p>
        </Reveal>
      </div>

      {related.length > 0 && (
        <div className="mt-24">
          <Reveal>
            <h2 className="font-serif text-2xl tracking-tight text-espresso sm:text-3xl">More {product.category.toLowerCase()}</h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-2 gap-x-5 gap-y-10 lg:grid-cols-4">
            {related.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
